import {
  BookOpen,
  CheckCircle2,
  ChevronDown,
  Circle,
  ClipboardList,
  DollarSign,
  HardHat,
  Search,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCompanies } from "@/hooks/use-companies";
import { useContactsByCompany } from "@/hooks/use-contacts";
import {
  type ContractorCommitment,
  type ContractorTask,
  type ProjectContractor,
  useAddProjectContractor,
  useContractorCommitments,
  useContractorTasks,
  useProjectContractors,
  useRemoveProjectContractor,
  useUpdateProjectContractor,
} from "@/hooks/use-project-contractors";

// ── Assign Contractor Dialog ──────────────────────────────────────────────────

function AssignContractorDialog({
  projectId,
  open,
  onOpenChange,
}: {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: allCompanies } = useCompanies();
  const { data: currentContractors } = useProjectContractors(projectId);
  const addContractor = useAddProjectContractor(projectId);
  const [search, setSearch] = useState("");

  const assignedIds = new Set((currentContractors ?? []).map((c) => c.contractorId));
  const filtered = (allCompanies ?? []).filter((c) => {
    if (assignedIds.has(c.id)) return false;
    const q = search.toLowerCase();
    return (
      !q ||
      (c.companyName ?? "").toLowerCase().includes(q) ||
      (c.trade ?? "").toLowerCase().includes(q) ||
      (c.contactPerson ?? "").toLowerCase().includes(q)
    );
  });

  function handleAssign(companyId: string) {
    addContractor.mutate(companyId, {
      onSuccess: () => onOpenChange(false),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-[#334155]">
            Assign Contractor
          </DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by company, trade, or contact..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="max-h-72 overflow-y-auto space-y-1">
          {filtered.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">
              No companies available to assign.
            </p>
          ) : (
            filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => handleAssign(c.id)}
                disabled={addContractor.isPending}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#415A77]/10">
                  <HardHat className="h-4 w-4 text-[#415A77]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 truncate">{c.companyName}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {[c.trade, c.contactPerson].filter(Boolean).join(" · ")}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Contact Selector ──────────────────────────────────────────────────────────

function ContactSelector({
  contractor,
  projectId,
}: {
  contractor: ProjectContractor;
  projectId: string;
}) {
  const { data: contacts, isLoading } = useContactsByCompany(contractor.contractorId);
  const updateContractor = useUpdateProjectContractor(projectId);

  const selectedContact = contacts?.find((c) => c.id === contractor.contactId);
  const label = selectedContact
    ? `${selectedContact.firstName ?? ""} ${selectedContact.lastName ?? ""}`.trim()
    : (contractor.contactPerson ?? "—");

  function handleSelect(contactId: string | null) {
    updateContractor.mutate({ id: contractor.id, contactId });
  }

  if (isLoading) return <span className="text-sm text-gray-400">Loading...</span>;
  if (!contacts || contacts.length === 0) {
    return <span className="text-sm text-gray-500">{contractor.contactPerson ?? "—"}</span>;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1 text-sm text-gray-700 hover:text-[#415A77] transition-colors group"
        >
          <span className="group-hover:underline underline-offset-2">{label}</span>
          <ChevronDown className="h-3 w-3 text-gray-400 flex-shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        <DropdownMenuLabel className="text-xs text-gray-500">Select contact</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {contacts.map((c) => {
          const name = `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim();
          const isSelected = contractor.contactId === c.id;
          return (
            <DropdownMenuItem
              key={c.id}
              onClick={() => handleSelect(c.id)}
              className="flex items-center gap-2"
            >
              {isSelected ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-[#415A77]" />
              ) : (
                <Circle className="h-3.5 w-3.5 text-gray-300" />
              )}
              <div className="min-w-0">
                <p className="font-medium truncate">{name}</p>
                {c.title && <p className="text-xs text-gray-500 truncate">{c.title}</p>}
              </div>
            </DropdownMenuItem>
          );
        })}
        {contractor.contactId && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleSelect(null)} className="text-gray-500 text-xs">
              <X className="h-3 w-3 mr-1" /> Clear selection
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Tasks Sheet ───────────────────────────────────────────────────────────────

const TASK_STATUS_COLORS: Record<string, string> = {
  completed: "bg-green-100 text-green-700",
  in_progress: "bg-blue-100 text-blue-700",
  blocked: "bg-red-100 text-red-700",
  overdue: "bg-orange-100 text-orange-700",
  not_started: "bg-gray-100 text-gray-600",
  skipped: "bg-gray-100 text-gray-400",
};

function TasksSheet({
  open,
  onOpenChange,
  contractor,
  projectId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  contractor: ProjectContractor;
  projectId: string;
}) {
  const {
    data: tasks,
    isLoading,
    isError,
    refetch,
  } = useContractorTasks(projectId, contractor.contractorId, open);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[33vw] min-w-[380px] overflow-y-auto">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle className="flex items-center gap-2 text-[#334155]">
            <ClipboardList className="h-5 w-5 text-[#415A77]" />
            Tasks — {contractor.companyName}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-2">
          {isLoading && (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          )}
          {isError && <ErrorState message="Failed to load tasks" onRetry={() => refetch()} />}
          {tasks && tasks.length === 0 && (
            <div className="py-12 text-center">
              <ClipboardList className="h-10 w-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No tasks assigned to this contractor.</p>
            </div>
          )}
          {tasks?.map((task) => (
            <TaskCard key={task.id} task={task} />
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function TaskCard({ task }: { task: ContractorTask }) {
  const status = task.status ?? "not_started";
  const colorClass = TASK_STATUS_COLORS[status] ?? "bg-gray-100 text-gray-600";
  const label = status.replace(/_/g, " ");

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-1">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-gray-900 leading-snug">{task.title ?? "Untitled"}</p>
        <span
          className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${colorClass}`}
        >
          {label}
        </span>
      </div>
      {task.phase && (
        <p className="text-xs text-gray-500 capitalize">{task.phase.replace(/_/g, " ")}</p>
      )}
      {task.dueDate && (
        <p className="text-xs text-gray-400">Due {new Date(task.dueDate).toLocaleDateString()}</p>
      )}
    </div>
  );
}

// ── Buy Log Sheet ─────────────────────────────────────────────────────────────

const COMMITMENT_STATUS_COLORS: Record<string, string> = {
  executed: "bg-green-100 text-green-700",
  pending_execution: "bg-amber-100 text-amber-700",
  draft: "bg-gray-100 text-gray-600",
  on_hold: "bg-orange-100 text-orange-700",
  closed: "bg-blue-100 text-blue-700",
  terminated: "bg-red-100 text-red-700",
  voided: "bg-gray-100 text-gray-400",
};

function formatAmount(val: string | null | undefined) {
  if (!val) return "—";
  const n = Number(val);
  if (Number.isNaN(n)) return val;
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function BuyLogSheet({
  open,
  onOpenChange,
  contractor,
  projectId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  contractor: ProjectContractor;
  projectId: string;
}) {
  const {
    data: commitments,
    isLoading,
    isError,
    refetch,
  } = useContractorCommitments(projectId, contractor.contractorId, open);

  const total = (commitments ?? []).reduce(
    (sum, c) => sum + Number(c.revisedAmount ?? c.originalAmount ?? 0),
    0,
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[33vw] min-w-[420px] overflow-y-auto">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle className="flex items-center gap-2 text-[#334155]">
            <DollarSign className="h-5 w-5 text-[#415A77]" />
            Buy Log — {contractor.companyName}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-2">
          {isLoading && (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20 w-full rounded-lg" />
              ))}
            </div>
          )}
          {isError && <ErrorState message="Failed to load commitments" onRetry={() => refetch()} />}
          {commitments && commitments.length === 0 && (
            <div className="py-12 text-center">
              <BookOpen className="h-10 w-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No commitments for this contractor.</p>
            </div>
          )}
          {commitments?.map((c) => (
            <CommitmentCard key={c.id} commitment={c} />
          ))}
          {commitments && commitments.length > 0 && (
            <div className="rounded-lg border border-[#415A77]/20 bg-[#415A77]/5 p-3 flex justify-between items-center">
              <span className="text-sm font-semibold text-[#334155]">Total Awarded</span>
              <span className="text-sm font-bold text-[#415A77]">
                {formatAmount(String(total))}
              </span>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function CommitmentCard({ commitment: c }: { commitment: ContractorCommitment }) {
  const status = c.status ?? "draft";
  const colorClass = COMMITMENT_STATUS_COLORS[status] ?? "bg-gray-100 text-gray-600";

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs text-gray-400 font-mono">{c.commitmentNumber}</p>
          <p className="text-sm font-medium text-gray-900 truncate">{c.title ?? "Untitled"}</p>
        </div>
        <span
          className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${colorClass}`}
        >
          {status.replace(/_/g, " ")}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-x-4 text-xs text-gray-500">
        <div>
          <span className="block text-gray-400">Contract Value</span>
          <span className="font-medium text-gray-700">
            {formatAmount(c.revisedAmount ?? c.originalAmount)}
          </span>
        </div>
        <div>
          <span className="block text-gray-400">Billed to Date</span>
          <span className="font-medium text-gray-700">{formatAmount(c.totalBilledToDate)}</span>
        </div>
      </div>
    </div>
  );
}

// ── Companies Panel ───────────────────────────────────────────────────────────

function CompaniesPanel({ projectId }: { projectId: string }) {
  const { data, isLoading, isError, error, refetch } = useProjectContractors(projectId);
  const updateContractor = useUpdateProjectContractor(projectId);
  const removeContractor = useRemoveProjectContractor(projectId);
  const [assignOpen, setAssignOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [tasksTarget, setTasksTarget] = useState<ProjectContractor | null>(null);
  const [buyLogTarget, setBuyLogTarget] = useState<ProjectContractor | null>(null);

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (isError) return <ErrorState message={error?.message} onRetry={() => refetch()} />;

  const contractors = data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-[#334155]">Companies & Contractors</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setAssignOpen(true)}
          className="rounded-lg"
        >
          <UserPlus className="mr-2 h-4 w-4" />
          Assign Contractor
        </Button>
      </div>

      {contractors.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
          <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No contractors assigned to this project yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Company
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Trade
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Contact
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Email
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-center">
                  AIA Doc
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500 text-center">
                  Lien Waiver
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Actions
                </TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {contractors.map((c) => (
                <TableRow key={c.id} className="hover:bg-gray-50">
                  <TableCell className="text-sm font-medium text-gray-900">
                    {c.companyName ?? "—"}
                  </TableCell>
                  <TableCell>
                    {(c.tradeCategoryName ?? c.trade) ? (
                      <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full border border-gray-200 text-gray-600">
                        {c.tradeCategoryName ?? c.trade}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <ContactSelector contractor={c} projectId={projectId} />
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">{c.email ?? "—"}</TableCell>
                  <TableCell className="text-center">
                    <Checkbox
                      checked={c.aiaDocReceived}
                      onCheckedChange={(checked) =>
                        updateContractor.mutate({
                          id: c.id,
                          aiaDocReceived: checked === true,
                        })
                      }
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <Checkbox
                      checked={c.lienWaiverReceived}
                      onCheckedChange={(checked) =>
                        updateContractor.mutate({
                          id: c.id,
                          lienWaiverReceived: checked === true,
                        })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-gray-500 hover:text-[#415A77] hover:bg-[#415A77]/5"
                        onClick={() => setTasksTarget(c)}
                      >
                        <ClipboardList className="h-3.5 w-3.5 mr-1" />
                        Tasks
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-gray-500 hover:text-[#415A77] hover:bg-[#415A77]/5"
                        onClick={() => setBuyLogTarget(c)}
                      >
                        <DollarSign className="h-3.5 w-3.5 mr-1" />
                        Buy Log
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50"
                      onClick={() => setDeleteTarget(c.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AssignContractorDialog
        projectId={projectId}
        open={assignOpen}
        onOpenChange={setAssignOpen}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Remove Contractor"
        description="Are you sure you want to remove this contractor from the project?"
        confirmLabel="Remove"
        variant="destructive"
        onConfirm={async () => {
          if (deleteTarget) {
            removeContractor.mutate(deleteTarget);
            setDeleteTarget(null);
          }
        }}
      />

      {tasksTarget && (
        <TasksSheet
          open={tasksTarget !== null}
          onOpenChange={(v) => {
            if (!v) setTasksTarget(null);
          }}
          contractor={tasksTarget}
          projectId={projectId}
        />
      )}

      {buyLogTarget && (
        <BuyLogSheet
          open={buyLogTarget !== null}
          onOpenChange={(v) => {
            if (!v) setBuyLogTarget(null);
          }}
          contractor={buyLogTarget}
          projectId={projectId}
        />
      )}
    </div>
  );
}

// ── Team Tab ──────────────────────────────────────────────────────────────────

export function TeamTab({ projectId }: { projectId: string }) {
  return <CompaniesPanel projectId={projectId} />;
}

// ── Project Directory (contractor view) ─────────────────────────────────────
// Stripped-down read-only directory: Company / Trade / Contact / Email only.
// No AIA / Lien Waiver columns, no Tasks / Buy Log actions, no delete or
// Assign buttons — contractors don't manage the project team.
export function ProjectDirectoryPanel({ projectId }: { projectId: string }) {
  const { data, isLoading, isError, error, refetch } = useProjectContractors(projectId);

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (isError) return <ErrorState message={error?.message} onRetry={() => refetch()} />;

  const contractors = data ?? [];

  return (
    <div className="space-y-4">
      <h3 className="text-base font-semibold text-[#334155]">Project Directory</h3>

      {contractors.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
          <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No companies on this project yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Company
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Trade
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Contact
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Email
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contractors.map((c) => (
                <TableRow key={c.id} className="hover:bg-gray-50">
                  <TableCell className="text-sm font-medium text-gray-900">
                    {c.companyName ?? "—"}
                  </TableCell>
                  <TableCell>
                    {(c.tradeCategoryName ?? c.trade) ? (
                      <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full border border-gray-200 text-gray-600">
                        {c.tradeCategoryName ?? c.trade}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-gray-700">{c.contactPerson ?? "—"}</TableCell>
                  <TableCell className="text-sm text-gray-600">{c.email ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
